import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Droplets, Eye, AlertTriangle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { saltFormationColors, metalReactivity } from '@/config/experiments/chem1131/experiment6.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens?: string; observe?: string[]; equation?: string }> = {
  1: {
    title: 'Preparing Metal Samples',
    content: 'We test five metals with different positions in the activity series: Cu (least reactive), Sn, Fe, Zn, and Al (most reactive among these). The activity series predicts which metals can displace hydrogen from acids.',
    whatHappens: 'Small amounts (roughly match-head size) of each metal are placed in separate test tubes for systematic comparison.',
    observe: ['Cu - reddish brown', 'Zn - silvery gray', 'Sn - silvery, soft', 'Al - silvery, lightweight', 'Fe - gray, magnetic']
  },
  2: {
    title: 'Testing with Water',
    content: 'Most metals do not react with water at room temperature. Only very reactive metals like Na and K react with cold water. This control test establishes a baseline.',
    whatHappens: 'Water is added to each tube to verify that none of these metals react with water under normal conditions.',
    observe: ['No bubbling in any tube', 'No color change', 'Metals remain intact'],
    equation: 'Metal + H₂O -> No reaction (for Cu, Zn, Sn, Al, Fe at room temp)'
  },
  3: {
    title: 'Observing Water Test (2 min)',
    content: 'A two-minute observation period ensures we do not miss any slow reactions. This is important because some reactions may have induction periods before becoming visible.',
    whatHappens: 'After waiting, we confirm that none of the metals show reactivity with water at room temperature.',
    observe: ['No bubbles after 2 minutes', 'Water remains clear', 'Metals show no change']
  },
  4: {
    title: 'Decanting the Water',
    content: 'Removing the water before adding acid ensures that the acid is not diluted, which would slow the reaction. It also removes any dissolved oxygen that could interfere.',
    whatHappens: 'The water is carefully poured off, leaving the dry metal pieces in each tube.',
    observe: ['Pour slowly to keep metal in tube', 'Tubes are now dry', 'Ready for acid addition']
  },
  5: {
    title: 'Adding HCl to Metals',
    content: 'Metals above hydrogen in the activity series can displace H+ ions from acids to form H₂ gas. The more reactive the metal, the faster and more vigorous the reaction.',
    whatHappens: '6 M HCl is added to each tube. Reactive metals immediately begin producing hydrogen gas bubbles.',
    observe: ['Work in fume hood - H₂ is flammable!', 'Some tubes bubble immediately', 'Cu shows no reaction'],
    equation: 'Metal + 2HCl -> MetalCl₂ + H₂(g)'
  },
  6: {
    title: 'Observing Reaction Rates',
    content: 'Reaction rate correlates with position in the activity series. Al and Zn react vigorously, Fe moderately, Sn slowly, and Cu not at all (it is below H in the series).',
    whatHappens: 'Comparing the bubbling intensity reveals the relative reactivity of each metal with acid.',
    observe: ['Al, Zn: vigorous bubbling', 'Fe: moderate bubbling', 'Sn: slow bubbling', 'Cu: no reaction!'],
    equation: 'Zn + 2HCl -> ZnCl₂ + H₂(g) (example)'
  },
  7: {
    title: 'Ranking by Reactivity',
    content: 'The activity series is a ranking of metals by their tendency to lose electrons and form cations. More reactive metals can displace less reactive metals from their compounds.',
    whatHappens: 'Based on observations, we rank the metals: Al > Zn > Fe > Sn > Cu (> H for reference).',
    observe: ['Al most reactive tested', 'Cu least reactive (below H)', 'This order matches the activity series'],
    equation: 'Activity: Al > Zn > Fe > Sn > H > Cu'
  }
};

// H2 Bubbles animation with variable intensity
function H2Bubbles({ active, intensity }: { active: boolean; intensity: 'none' | 'slow' | 'moderate' | 'vigorous' }) {
  if (!active || intensity === 'none') return null;

  const count = intensity === 'vigorous' ? 15 : intensity === 'moderate' ? 8 : 4;
  const speed = intensity === 'vigorous' ? 0.5 : intensity === 'moderate' ? 0.8 : 1.2;

  return (
    <g>
      {[...Array(count)].map((_, i) => (
        <circle
          key={i}
          cx={15 + (i % 5) * 6}
          cy={70}
          r={1.5 + (i % 3)}
          fill="rgba(255,255,255,0.6)"
        >
          <animate
            attributeName="cy"
            values="70;25;70"
            dur={`${speed + (i % 3) * 0.15}s`}
            repeatCount="indefinite"
            begin={`${i * 0.1}s`}
          />
          <animate
            attributeName="opacity"
            values="0.6;0.1;0.6"
            dur={`${speed + (i % 3) * 0.15}s`}
            repeatCount="indefinite"
            begin={`${i * 0.1}s`}
          />
        </circle>
      ))}
    </g>
  );
}

export default function acidMetal() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [metalsPrepared, setMetalsPrepared] = useState(false);
  const [waterAdded, setWaterAdded] = useState(false);
  const [waterObserved, setWaterObserved] = useState(false);
  const [waterDecanted, setWaterDecanted] = useState(false);
  const [hclAdded, setHclAdded] = useState(false);
  const [reactionsObserved, setReactionsObserved] = useState(false);
  const [reactivityRanked, setReactivityRanked] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [reacting, setReacting] = useState(false);
  const id = useId();

  const metals = ['Cu', 'Zn', 'Sn', 'Al', 'Fe'];
  const completedSteps = [metalsPrepared, waterAdded, waterObserved, waterDecanted, hclAdded, reactionsObserved, reactivityRanked].filter(Boolean).length;

  const handlePrepareMetals = useCallback(() => {
    if (currentStep === 1 && !metalsPrepared) {
      setMetalsPrepared(true);
      setCurrentStep(2);
    }
  }, [currentStep, metalsPrepared]);

  const handleAddWater = useCallback(() => {
    if (currentStep === 2 && metalsPrepared && !waterAdded) {
      setWaterAdded(true);
      setCurrentStep(3);
    }
  }, [currentStep, metalsPrepared, waterAdded]);

  const handleObserveWater = useCallback(() => {
    if (currentStep === 3 && waterAdded && !waterObserved) {
      setWaterObserved(true);
      setCurrentStep(4);
    }
  }, [currentStep, waterAdded, waterObserved]);

  const handleDecantWater = useCallback(() => {
    if (currentStep === 4 && waterObserved && !waterDecanted) {
      setWaterDecanted(true);
      setCurrentStep(5);
    }
  }, [currentStep, waterObserved, waterDecanted]);

  const handleAddHCl = useCallback(() => {
    if (currentStep === 5 && waterDecanted && !hclAdded) {
      setHclAdded(true);
      setReacting(true);
      setCurrentStep(6);
    }
  }, [currentStep, waterDecanted, hclAdded]);

  const handleObserveReactions = useCallback(() => {
    if (currentStep === 6 && hclAdded && !reactionsObserved) {
      setReactionsObserved(true);
      setCurrentStep(7);
    }
  }, [currentStep, hclAdded, reactionsObserved]);

  const handleRankReactivity = useCallback(() => {
    if (currentStep === 7 && reactionsObserved && !reactivityRanked) {
      setReactivityRanked(true);
    }
  }, [currentStep, reactionsObserved, reactivityRanked]);

  const getMetalColor = (symbol: string) => {
    const metal = metalReactivity[symbol.toLowerCase()];
    return metal ? metal.color : '#888';
  };

  const getReactionIntensity = (symbol: string): 'none' | 'slow' | 'moderate' | 'vigorous' => {
    const metal = metalReactivity[symbol.toLowerCase()];
    return metal ? metal.reactionIntensity : 'none';
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part E: Acid + Metal</h3>
        </div>
        <div className="safety-warning">
          <AlertTriangle size={14} />
          <span>FUME HOOD - H2 gas is flammable!</span>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Prepare Metal Samples', hint: 'Add match-head sized Cu, Zn, Sn, Al, Fe', done: metalsPrepared },
            { id: 2, title: 'Test with Water', hint: 'Add 2 mL water to each', done: waterAdded },
            { id: 3, title: 'Observe (2 min)', hint: 'Watch for any reaction', done: waterObserved },
            { id: 4, title: 'Decant Water', hint: 'Remove water from tubes', done: waterDecanted },
            { id: 5, title: 'Add HCl', hint: 'Add 2 mL 6 M HCl', done: hclAdded },
            { id: 6, title: 'Observe Reactions', hint: 'Compare reaction intensity', done: reactionsObserved },
            { id: 7, title: 'Rank by Reactivity', hint: 'Order metals by activity', done: reactivityRanked }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Reactivity ranking */}
        {reactivityRanked && (
          <div className="reactivity-ranking">
            <h4>Reactivity Order:</h4>
            <div className="ranking">
              <span className="metal high" style={{ background: getMetalColor('Al') }}>Al</span>
              <span className="arrow">&gt;</span>
              <span className="metal high" style={{ background: getMetalColor('Zn') }}>Zn</span>
              <span className="arrow">&gt;</span>
              <span className="metal med" style={{ background: getMetalColor('Fe') }}>Fe</span>
              <span className="arrow">&gt;</span>
              <span className="metal low" style={{ background: getMetalColor('Sn') }}>Sn</span>
              <span className="arrow">&gt;</span>
              <span className="metal none" style={{ background: getMetalColor('Cu') }}>Cu</span>
            </div>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Metal Activity Series"
        completedCount={completedSteps}
        totalSteps={7}
        equipment={['Cu', 'Zn', 'Sn', 'Al', 'Fe', 'Water', '6 M HCl']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* Metal test tubes */}
            <div
              className={`test-tube-rack ${currentStep >= 1 && currentStep <= 6 ? 'clickable' : ''}`}
              onClick={() => {
                if (currentStep === 1) handlePrepareMetals();
                else if (currentStep === 2) handleAddWater();
                else if (currentStep === 3) handleObserveWater();
                else if (currentStep === 4) handleDecantWater();
                else if (currentStep === 5) handleAddHCl();
                else if (currentStep === 6) handleObserveReactions();
              }}
            >
              {metals.map((metal, index) => (
                <div key={metal} className="tube-container">
                  <svg viewBox="0 0 45 100" width={40} height={95}>
                    <defs>
                      <linearGradient id={`tube-metal-${id}-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                        <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                        <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                      </linearGradient>
                    </defs>

                    {/* Tube rim */}
                    <ellipse cx="22" cy="10" rx="10" ry="3" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1" />

                    {/* Tube body */}
                    <path
                      d="M12 10 L12 75 Q12 88 22 88 Q32 88 32 75 L32 10"
                      fill={`url(#tube-metal-${id}-${index})`}
                      stroke="#8BA8C4"
                      strokeWidth="1"
                    />

                    {/* Liquid (water or HCl) */}
                    {(waterAdded || hclAdded) && !waterDecanted && (
                      <path
                        d="M14 40 L14 73 Q14 84 22 84 Q30 84 30 73 L30 40 Z"
                        fill="rgba(200,230,255,0.5)"
                      />
                    )}
                    {hclAdded && waterDecanted && (
                      <path
                        d="M14 40 L14 73 Q14 84 22 84 Q30 84 30 73 L30 40 Z"
                        fill="rgba(255,255,220,0.6)"
                      />
                    )}

                    {/* Metal piece */}
                    {metalsPrepared && (
                      <ellipse
                        cx="22"
                        cy="78"
                        rx="6"
                        ry="4"
                        fill={getMetalColor(metal)}
                      />
                    )}

                    {/* H2 Bubbles when HCl added */}
                    {hclAdded && reacting && (
                      <H2Bubbles active={true} intensity={getReactionIntensity(metal)} />
                    )}

                    {/* Glass shine */}
                    <line x1="14" y1="18" x2="14" y2="65" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round" />

                    <text x="22" y="98" fontSize="7" fill="#E0E0E0" textAnchor="middle">{metal}</text>

                    {/* Reaction indicator */}
                    {hclAdded && reactionsObserved && (
                      <text
                        x="22"
                        y="35"
                        fontSize="5"
                        fill={getReactionIntensity(metal) === 'none' ? '#888' : getReactionIntensity(metal) === 'vigorous' ? '#FF5A36' : '#60A5FA'}
                        textAnchor="middle"
                      >
                        {getReactionIntensity(metal) === 'none' ? 'No rxn' :
                         getReactionIntensity(metal) === 'vigorous' ? 'Fast!' :
                         getReactionIntensity(metal) === 'moderate' ? 'Med' : 'Slow'}
                      </text>
                    )}
                  </svg>
                </div>
              ))}
            </div>

            {/* HCl Bottle */}
            <div className="equipment-station">
              <svg viewBox="0 0 50 80" width={45} height={75}>
                <rect x="12" y="20" width="26" height="50" fill="#FFFACD" stroke="#888" strokeWidth="1" rx="2" />
                <rect x="17" y="10" width="16" height="12" fill="#AA4444" rx="2" />
                <text x="25" y="48" fontSize="6" fill="#333" textAnchor="middle">HCl</text>
                <text x="25" y="56" fontSize="5" fill="#666" textAnchor="middle">6 M</text>
                <text x="25" y="78" fontSize="7" fill="#E0E0E0" textAnchor="middle">Acid</text>
              </svg>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !metalsPrepared && (
              <div className="status-message info">
                <Droplets size={20} />
                Click tubes to add match-head sized samples of Cu, Zn, Sn, Al, Fe
              </div>
            )}
            {currentStep === 2 && !waterAdded && (
              <div className="status-message info">
                <Droplets size={20} />
                Click to add 2 mL deionized water to each tube
              </div>
            )}
            {currentStep === 3 && !waterObserved && (
              <div className="status-message info">
                <Eye size={20} />
                Click to observe - none of these metals react with water at room temp
              </div>
            )}
            {currentStep === 4 && !waterDecanted && (
              <div className="status-message info">
                <Droplets size={20} />
                Click to decant (pour off) the water from each tube
              </div>
            )}
            {currentStep === 5 && !hclAdded && (
              <div className="status-message warning">
                <AlertTriangle size={20} />
                In fume hood: Click to add 6 M HCl to each tube
              </div>
            )}
            {currentStep === 6 && !reactionsObserved && (
              <div className="status-message info">
                <Eye size={20} />
                Click to observe: Cu - no rxn; Zn, Al - vigorous; Fe - moderate; Sn - slow
              </div>
            )}
            {currentStep === 7 && !reactivityRanked && (
              <div
                className="status-message success"
                onClick={handleRankReactivity}
                style={{ cursor: 'pointer' }}
              >
                <Check size={20} />
                Click to rank: Al &gt; Zn &gt; Fe &gt; Sn &gt; Cu (H)
              </div>
            )}
            {reactivityRanked && (
              <div className="status-message success">
                <Check size={20} />
                Complete! More active metals displace H₂ from acids more vigorously.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Metal Activity Series</h4>
            <div className="equation">
              Zn + 2HCl → ZnCl₂ + H₂(g)
            </div>
            <p>
              Metals above hydrogen in the activity series can displace H+ ions from acids.
              Cu is below H, so it doesn't react with HCl. The more reactive the metal,
              the more vigorous the H₂ evolution.
            </p>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            align-items: flex-end;
            justify-content: center;
          }

          .test-tube-rack {
            display: flex;
            gap: 8px;
            padding: 15px;
            background: rgba(60, 60, 60, 0.3);
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .test-tube-rack.clickable {
            cursor: pointer;
          }

          .test-tube-rack.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .tube-container {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
          }

          .safety-warning {
            display: flex;
            align-items: center;
            gap: 8px;
            background: rgba(239, 68, 68, 0.2);
            color: #EF4444;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 11px;
            margin: 8px 0;
          }

          .reactivity-ranking {
            margin-top: 16px;
            padding: 12px;
            background: var(--bg-elevated);
            border-radius: 6px;
          }

          .reactivity-ranking h4 {
            margin: 0 0 10px 0;
            font-size: 12px;
            color: var(--text-primary);
          }

          .ranking {
            display: flex;
            align-items: center;
            gap: 4px;
            flex-wrap: wrap;
          }

          .ranking .metal {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: bold;
            color: #FFF;
          }

          .ranking .metal.high {
            box-shadow: 0 0 6px rgba(255, 90, 54, 0.5);
          }

          .ranking .metal.med {
            box-shadow: 0 0 4px rgba(96, 165, 250, 0.5);
          }

          .ranking .arrow {
            color: var(--text-secondary);
            font-size: 10px;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.warning {
            background: rgba(245, 158, 11, 0.2);
            color: #FBBF24;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box .equation {
            font-family: 'Courier New', monospace;
            background: rgba(0, 0, 0, 0.3);
            padding: 8px 12px;
            border-radius: 4px;
            margin-bottom: 8px;
            color: #A0A0A0;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp6"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
